import apiClient from "../core/config/api";

export interface VerifierResultRow {
    email?: string | null;
    status?: string | null;
    message?: string | null;
    reason?: string | null;
    domain?: string | null;
    catch_all?: boolean | string | null;
    is_catch_all?: boolean | string | null;
    spf?: boolean | string | null;
    has_spf?: boolean | string | null;
    dmarc?: boolean | string | null;
    has_dmarc?: boolean | string | null;
}

export interface VerifierStatusCount {
    key: string;
    label?: string;
    count: number;
}

export interface VerifierResultsPage {
    data?: VerifierResultRow[];
    total_pages?: number;
    distinct_statuses?: VerifierStatusCount[];
    file_name?: string;
}

// The shared results route serves the request's owner as well as admins.
// include_dns adds SPF/DMARC presence for the page's domains (results page).
export const fetchVerifierResults = async (
    requestId: number,
    params: { page: number; page_size: number; status?: string; include_dns?: boolean }
): Promise<VerifierResultsPage> => {
    const response = await apiClient.get(`/verifier-requests/${requestId}/view-results`, { params });
    return response.data || {};
};

export type ResultBucket = "valid" | "invalid" | "catchall" | "donotsend" | "domain" | "risky" | "other";

export const bucketForStatus = (rawStatus?: string | null): ResultBucket => {
    const status = (rawStatus || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
    // Risky-Valid is reported as Valid.
    if (status === "valid" || status === "verified" || status === "risky_valid") return "valid";
    if (status === "invalid") return "invalid";
    if (status === "catch_all" || status === "catchall") return "catchall";
    if (["do_not_send", "do_not_mail", "role", "role_based", "unknown"].includes(status)) return "donotsend";
    if (["invalid_domain", "no_mx", "domain_invalid"].includes(status)) return "domain";
    if (["risky", "blocklisted"].includes(status)) return "risky";
    return "other";
};

export const BUCKET_LABELS: Record<ResultBucket, string> = {
    valid: "Valid",
    invalid: "Invalid",
    catchall: "Catch-All",
    donotsend: "Do Not Send",
    domain: "Invalid Domain",
    risky: "Risky",
    other: "",
};

const RESOLVABLE_PAGE_SIZE = 500;
// Safety cap on how many addresses the picker loads.
export const RESOLVABLE_MAX_EMAILS = 10000;

/**
 * Every Catch-All and Risky address in a request - the ones the Catch-All
 * Resolver can re-check. Reads the per-status keys from the first page so it
 * filters by whatever spelling the backend uses (catch_all / catch-all ...).
 */
export const fetchResolvableEmails = async (requestId: number): Promise<{ email: string; status: string }[]> => {
    const first = await fetchVerifierResults(requestId, { page: 1, page_size: 1 });
    const keys = (first.distinct_statuses || [])
        .map((entry) => entry.key)
        .filter((key) => {
            const bucket = bucketForStatus(key);
            return bucket === "catchall" || bucket === "risky";
        });

    const results: { email: string; status: string }[] = [];
    for (const status of keys) {
        let page = 1;
        let totalPages = 1;
        do {
            const data = await fetchVerifierResults(requestId, { page, page_size: RESOLVABLE_PAGE_SIZE, status });
            (data.data || []).forEach((row) => {
                if (row.email) results.push({ email: row.email, status: row.status || status });
            });
            totalPages = data.total_pages || 1;
            page += 1;
        } while (page <= totalPages && results.length < RESOLVABLE_MAX_EMAILS);
        if (results.length >= RESOLVABLE_MAX_EMAILS) break;
    }
    return results.slice(0, RESOLVABLE_MAX_EMAILS);
};
